import type { PullRequestDetail } from "@afar-research/contracts";
import { PlusIcon } from "lucide-react";
import { useState } from "react";

import { newCommandId } from "../../lib/utils";
import { executePullRequestDiscussion } from "../../pullRequestDiscussionCommand";
import { invalidatePullRequest } from "../../pullRequestInvalidation";
import type { PullRequestSurfaceDescriptor } from "../../pullRequestSurfaceStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { PullRequestActorAvatar } from "./pullRequestPresentation";

/**
 * Who has been asked to review, and the way to ask someone else.
 *
 * Asking only ever adds: the host's request is a union, so this cannot take a request back and
 * does not offer to. A reviewer already asked is not offered again, because asking twice is not
 * a thing the reader can mean.
 */
export function PullRequestReviewerPicker({
  descriptor,
  detail,
  enabled,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
  readonly enabled: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const canAsk =
    enabled &&
    detail.viewerPermissions.requestReviewers &&
    detail.state === "open" &&
    !detail.isDraft &&
    detail.projectRevision !== undefined;

  const ask = (name: string) => {
    if (!canAsk || pending || detail.projectRevision === undefined) return;
    // A slash is how GitHub writes a team, and teams and people are asked by different fields.
    const team = name.includes("/");
    setPending(true);
    void executePullRequestDiscussion({
      environmentId: descriptor.environmentId,
      projectId: detail.projectId,
      repository: detail.repository,
      number: detail.number,
      expectedRevision: detail.projectRevision,
      resourceVersion: detail.resourceVersion,
      commandId: newCommandId(),
      action: {
        kind: "request-reviewers",
        users: team ? [] : [name],
        teams: team ? [name] : [],
      },
    })
      .then((result) => {
        if (result.status === "settled" && result.receipt.status === "accepted") {
          invalidatePullRequest({
            environmentId: descriptor.environmentId,
            projectId: detail.projectId,
            repository: detail.repository,
            number: detail.number,
            partitions: ["summary", "list"],
          });
        }
      })
      .finally(() => setPending(false));
  };

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {detail.reviewers.length === 0 ? (
        <span className="text-muted-foreground">None</span>
      ) : (
        detail.reviewers.map((reviewer) => (
          <span className="inline-flex items-center gap-1" key={reviewer.login}>
            <PullRequestActorAvatar actor={reviewer} className="size-3.5" />
            <span className="max-w-40 truncate">{reviewer.login}</span>
          </span>
        ))
      )}
      {canAsk ? (
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setDraft("");
          }}
        >
          <PopoverTrigger
            render={
              <Button
                aria-label="Request a reviewer"
                disabled={pending}
                size="icon-xs"
                variant="ghost"
              />
            }
          >
            <PlusIcon aria-hidden className="size-3.5" />
          </PopoverTrigger>
          <PopoverPopup align="start" className="w-64 p-2" side="bottom">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const name = draft.trim().replace(/^@/u, "");
                if (
                  name.length === 0 ||
                  detail.reviewers.some(
                    (reviewer) => reviewer.login.toLowerCase() === name.toLowerCase(),
                  )
                ) {
                  return;
                }
                ask(name);
                setDraft("");
                setOpen(false);
              }}
            >
              <Input
                aria-label="Reviewer to request"
                autoFocus
                onChange={(event) => setDraft(event.target.value)}
                placeholder="username or org/team"
                value={draft}
              />
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                A request cannot be taken back from here.
              </p>
            </form>
          </PopoverPopup>
        </Popover>
      ) : null}
    </span>
  );
}
